import { z } from "zod";

/**
 * Shared frontmatter primitives.
 *
 * Every entity's frontmatter builds on these: stable UUID, kebab-case slug,
 * ISO-8601 timestamps, optional prompt-registry audit trail.
 */

/** ISO-8601 timestamp string (UTC recommended). */
export const isoTimestamp = z.string().datetime({ offset: true });

/**
 * Kebab-case slug: lowercase letters, digits, hyphens. Must match filename
 * basename.
 *
 * Multiple dashes between alphanumeric groups are allowed so compound
 * identifiers like a scenario's `<persona>--<outline>` slug validate cleanly.
 * Leading/trailing dashes remain forbidden.
 */
export const slugSchema = z
  .string()
  .min(1)
  .regex(/^[a-z0-9]+(-+[a-z0-9]+)*$/, {
    message:
      "slug must be lowercase kebab-case (letters, digits, dashes between alphanumeric groups; no leading/trailing dashes)",
  });

/** UUID v4-ish; accepts any valid UUID string. */
export const uuidSchema = z.string().uuid();

/**
 * Audit trail for generated entities — records which prompt-registry entry
 * produced the content. Omitted when the user created the entity by hand.
 *
 * See `local-first-foundation` Decision 4 and `local-llm-config` spec.
 */
export const generatedBySchema = z.object({
  /** Matches a `PromptEntry.id` in `packages/ai/src/prompt-registry.ts`. */
  id: z.string().min(1),
  /** Semver string matching `PromptEntry.version` at the time of generation. */
  version: z.string().regex(/^\d+\.\d+\.\d+(?:-[A-Za-z0-9.-]+)?$/, {
    message: "version must be semver (e.g. 1.0.0)",
  }),
});
export type GeneratedBy = z.infer<typeof generatedBySchema>;

/**
 * Base identity fields present on every entity frontmatter.
 *
 * Each concrete entity extends this with its own fields.
 */
export const entityBaseSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  created_at: isoTimestamp.nullish(),
  updated_at: isoTimestamp.nullish(),
  /** UUID of the corresponding cloud row when this project is cloud-linked. */
  cloud_id: uuidSchema.nullish(),
  /** Who authored the entity — filled in during the cloud-link step in `/iamu:init` when identity is known. */
  created_by: z.string().nullish(),
  /** Prompt-registry audit trail. Absent when hand-written. */
  generated_by: generatedBySchema.nullish(),
});
export type EntityBase = z.infer<typeof entityBaseSchema>;
